import type { Camera, Mesh, BufferAttribute } from 'three'
import { Line3, Plane, Vector2, Vector3, Box3 } from 'three'

// memory-allocated objects for shared use
const renderPlane = new Plane()
const laser = new Line3()

const b1 = new Vector3()
const b2 = new Vector3()
const b3 = new Vector3()
const xy0 = new Vector3()
const x1 = new Vector3()
const y1 = new Vector3()

const v1 = new Vector3()
const v2 = new Vector3()

function _getBasis(plane: Plane, camera: Camera) {
  xy0.set(0, 0, 0.5).unproject(camera)
  x1.set(1, 0, 0.5).unproject(camera)
  y1.set(0, -1, 0.5).unproject(camera)

  // form a normalized basis for the render plane
  const center = renderPlane.projectPoint(xy0, b1)
  const basisX = renderPlane.projectPoint(x1, b2).sub(center).normalize()
  const basisY = renderPlane.projectPoint(y1, b3).sub(center).normalize()

  return { basisX, basisY }
}

export function projectFlagpole(origin: Vector3, flagpole: Vector2, camera: Camera): Vector3 {
  const cameraDirection = camera.getWorldDirection(v1)
  renderPlane.setFromNormalAndCoplanarPoint(cameraDirection, origin)

  const { basisX, basisY } = _getBasis(renderPlane, camera)

  const flagpoleEnd = v1.copy(origin)

  flagpoleEnd.addScaledVector(basisX, flagpole.x) // translate horizontally
  flagpoleEnd.addScaledVector(basisY, flagpole.y) // translate vertically

  return flagpoleEnd.clone().project(camera)
}

export function moveOnPlane(origin: Vector3, from: Vector2, to: Vector2, camera: Camera): Vector3 {
  const cameraDirection = camera.getWorldDirection(v1)
  renderPlane.setFromNormalAndCoplanarPoint(cameraDirection, origin)

  const laserDotFrom = mouseClickOnPlane(renderPlane, from, camera)
  const laserDotTo = mouseClickOnPlane(renderPlane, to, camera)
  const laserDelta = laserDotTo.sub(laserDotFrom)
  return v1.copy(origin).add(laserDelta)
}

export function flagpoleMidpoint(origin: Vector3, flagpole: Vector2, camera: Camera): Vector3 {
  const cameraDirection = camera.getWorldDirection(v1)
  renderPlane.setFromNormalAndCoplanarPoint(cameraDirection, origin)

  const { basisX, basisY } = _getBasis(renderPlane, camera)

  const flagpoleMid = v1.copy(origin)

  flagpoleMid.addScaledVector(basisX, flagpole.x / 2) // translate horizontally
  flagpoleMid.addScaledVector(basisY, flagpole.y / 2) // translate vertically

  return flagpoleMid.clone()
}

export function mouseClickOnPlane(plane: Plane, normalized: Vector2, camera: Camera) {
  laser.start.copy(camera.position) // pew pew at the mouse click

  // convert to world space
  laser.end.set(normalized.x, normalized.y, 0.5).unproject(camera)

  // find the intersection of the laser and the plane
  // do not use Plane.intersectLine() because we want to use an infinite line

  const laserDelta = laser.delta(v1) // stores the laser start -> end vector

  const denominator = plane.normal.dot(laserDelta)

  if (plane.distanceToPoint(laser.start) === 0) {
    throw new Error('line is coplanar, did you place a label on the camera lens?')
  }
  if (denominator === 0) {
    throw new Error('unknown error')
  }

  const t = -(laser.start.dot(plane.normal) + plane.constant) / denominator

  // store the point where the laser hits the render plane
  return v2.clone().copy(laser.start).addScaledVector(laserDelta, t)
}

export function unprojectFlagpole(origin: Vector3, normalized: Vector2, camera: Camera): Vector2 {
  const cameraDirection = camera.getWorldDirection(v1)
  renderPlane.setFromNormalAndCoplanarPoint(cameraDirection, origin)

  // store the point where the laser hits the render plane
  const laserDot = mouseClickOnPlane(renderPlane, normalized, camera)

  // flagpole vector in 3D world space
  const flagpole = laserDot.sub(origin)

  // convert to plane space
  const { basisX, basisY } = _getBasis(renderPlane, camera)

  // basisX * x0 + basisY * y0 = flagpole.x
  // (flagpole.x - basisY.x * y0) * basisX.y = (flagpole.y - basisY.y * y0) * basisX.x
  // flagpole.x * basisX.y - basisY.x * y0 * basisX.y = flagpole.y * basisX.x - basisY.y * y0 * basisX.x
  // flagpole.x * basisX.y - flagpole.y * basisX.x = basisY.x * y0 * basisX.y - basisY.y * y0 * basisX.x
  // (flagpole.x * basisX.y - flagpole.y * basisX.x) / (basisY.x * basisX.y - basisY.y * basisX.x) = y0
  // y0 = (flagpole.y * basisX.x - flagpole.x * basisX.y) / (basisY.y * basisX.x - basisY.x * basisX.y)

  const y0 = (basisX.x * flagpole.y - basisX.y * flagpole.x) / (basisX.x * basisY.y - basisX.y * basisY.x)
  const x0 = (flagpole.x - basisY.x * y0) / basisX.x

  return new Vector2(x0, -y0) // flip Y
}

export function getClosestVertex(mesh: Mesh, point: Vector3): { pos: Vector3; index: number } {
  const positionAttributes = mesh.geometry.getAttribute('position') as BufferAttribute
  const positions = positionAttributes.array

  let closestDistance = Number.MAX_VALUE
  let closestVertex: Vector3 | null = null
  let closestVertexIndex: number = -1

  for (let i = 0, len = positionAttributes.count; i < len; i++) {
    let vertex = new Vector3().fromArray(positions, i * 3)
    vertex = mesh.localToWorld(vertex)
    const distance = vertex.distanceToSquared(point)

    if (distance < closestDistance) {
      closestDistance = distance
      closestVertex = vertex.clone()
      closestVertexIndex = i
    }
  }

  return { pos: closestVertex as Vector3, index: closestVertexIndex }
}

export function getVertex(mesh: Mesh, vertexIndex: number): Vector3 {
  const positionAttributes = mesh.geometry.getAttribute('position') as BufferAttribute
  const positions = positionAttributes.array
  const vertex = new Vector3().fromArray(positions, vertexIndex * 3)

  return mesh.localToWorld(vertex)
}

export function getMeshSize(mesh: Mesh, camera: Camera): Vector3 {
  const bbox = new Box3().setFromObject(mesh)
  const size = new Vector3()
  bbox.getSize(size)

  return size
}
